import { useIsMutating, useMutation } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { toast } from "@/components/toast/toast-queue";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import { useNavigateToRequestList } from "./useNavigateToRequestList";

export function useCreateDraft() {
  const { data: userData } = useUser();
  const projectId = useGetCurrentProjectId();
  const navigateToRequestList = useNavigateToRequestList();
  const {
    ["draft_id"]: draftId,
    ["duplicate_from"]: duplicateFrom,
    ["request-type"]: requestType,
  } = useSearch({
    from: "/_auth/_app-shell/requests/create",
  });

  const createDraft = useMutation({
    mutationFn: (projectId: number) =>
      apiClient
        .url("/requests")
        .post({
          projectId,
          requestType,
        })
        .json(),
    mutationKey: [
      "draftId",
      { projectId: projectId, userId: userData?.user.id },
    ],
    onError: (error: {
      error: string;
      message: string;
      statusCode: number;
    }) => {
      if (error.statusCode === 400) {
        const errorMessage = error.message;

        if (
          [
            "No credits available",
            "Subscription ended",
            "Subscription is cancelled",
          ].includes(errorMessage)
        ) {
          navigateToRequestList();

          return;
        }
      }

      // Handle kickstarter draft limit
      if (error.statusCode === 403) {
        toast.add(
          {
            description: error.message,
            title: "Draft limit reached",
            type: "error",
          },
          {
            timeout: 8000,
          },
        );
        // Redirect back to requests page
        setTimeout(() => {
          navigateToRequestList();
        }, 2000);

        return;
      }

      toast.add(
        {
          description: "Please try again later",
          title: "Error creating draft",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    retry: 3,
  });

  const createdDraftForProjectRef = useRef<number>();

  useEffect(() => {
    if (!projectId || duplicateFrom || draftId) return;
    if (createdDraftForProjectRef.current === projectId) return;

    createdDraftForProjectRef.current = projectId;
    createDraft.mutate(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, duplicateFrom, draftId]);

  const isPending = useIsMutating({ mutationKey: ["draftId"] }) > 0;

  return { ...createDraft, isPending };
}
